import { useEffect, useState } from "react";
import { fetchCierreMes, fetchGlobalCierreMes } from "../lib/api";
import { useSnapshot } from "../contexts/SnapshotContext";
import { formatEUR, formatNum, RANK_LABELS } from "../lib/format";
import { RankBadge } from "../components/RankBadge";
import {
  Crown,
  WarningCircle,
  Users,
  TrendUp,
  Clock,
  CheckCircle,
  ArrowsClockwise,
  CaretRight,
  CaretDown,
  Confetti,
  Sparkle,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const rankBg = {
  "Lider Junior": "bg-[#E3A569]",
  "Lider Ejecutivo": "bg-[#C57B67]",
  "Lider Master": "bg-[#8A9A86]",
};

function ProgressBar({ value, met }) {
  const color = met ? "bg-[#8A9A86]" : value >= 75 ? "bg-[#E3A569]" : value >= 40 ? "bg-[#C57B67]" : "bg-[#B85C5C]";
  return (
    <div className="h-1.5 w-full bg-[#F2EFEA] rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  );
}

function fmtFalta(req) {
  if (req.met) return "✓ cumplido";
  const f = req.faltan;
  if (req.unit === "€") return `Faltan ${formatEUR(f)}`;
  return `Faltan ${formatNum(f)}`;
}

function PromoCard({ item, ready = false }) {
  const [open, setOpen] = useState(ready);
  return (
    <div
      className={`bg-white border rounded-xl overflow-hidden transition-all ${
        ready ? "border-[#8A9A86]/40 ring-1 ring-[#8A9A86]/20" : "border-[#E5E0D8] hover:border-[#D5CFC5]"
      }`}
      data-testid={`promo-${item.id_consultor}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <div className={`w-1 self-stretch rounded-full ${rankBg[item.target_rank] || "bg-[#C57B67]"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-[#2D2A26]">{item.nome}</p>
            <RankBadge rank={item.titulo_actual} />
            <span className="text-[#736F68] text-xs">→</span>
            <RankBadge rank={item.target_rank} />
            {ready && (
              <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#4A5546] bg-[#8A9A86]/20 px-1.5 py-0.5 rounded">
                <Confetti size={11} weight="fill" />
                ¡cumple ya!
              </span>
            )}
            {item.coordenador && (
              <span className="text-[10px] text-[#736F68] bg-[#F2EFEA] px-1.5 py-0.5 rounded border border-[#E5E0D8]">
                {item.coordenador}
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1 max-w-xs">
              <ProgressBar value={item.global_progress} met={item.all_met} />
            </div>
            <p className="text-xs font-mono text-[#736F68]">
              {item.met_count}/{item.total_requirements} requisitos · {item.global_progress}%
            </p>
          </div>
        </div>
        <span className="text-[#736F68]">
          {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-0 border-t border-[#F2EFEA] bg-[#F9F8F6]/50">
          <ul className="mt-4 space-y-3">
            {item.requirements.map((r, idx) => (
              <li key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_auto_120px] gap-3 items-center">
                <div>
                  <p className="text-sm font-medium text-[#2D2A26]">{r.label}</p>
                  <p className="text-xs text-[#736F68] font-mono mt-0.5">
                    Actual: {r.unit === "€" ? formatEUR(r.current) : formatNum(r.current)}
                    {" · "}
                    {fmtFalta(r)}
                  </p>
                </div>
                <span className={`text-xs font-semibold ${r.met ? "text-[#4A5546]" : "text-[#736F68]"}`}>
                  {r.progress}%
                </span>
                <ProgressBar value={r.progress} met={r.met} />
              </li>
            ))}
          </ul>
          {item.lider_subequipa && (
            <p className="mt-4 text-xs text-[#736F68]">Líder directa: {item.lider_subequipa}</p>
          )}
        </div>
      )}
    </div>
  );
}

const MONTH_ABBR = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTH_LONG = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const MONTH_STATUS_LABEL = {
  met: "Cumplió el objetivo del rango",
  failed: "Falló el objetivo del rango",
  before_change: "Rango anterior (no cuenta)",
  future: "Todavía no llega",
  no_data: "Sin datos ese mes",
};
const MONTH_STATUS_STYLE = {
  met: "bg-[#8A9A86]",
  failed: "bg-[#B85C5C]",
  before_change: "bg-[#E5E0D8]",
  future: "bg-white border border-dashed border-[#E5E0D8]",
  no_data: "bg-white border border-dashed border-[#D5CFC5]",
};

function StatusCalendar({ months }) {
  if (!months || months.length === 0) return null;
  return (
    <div className="mt-3 flex items-center gap-1" data-testid="status-calendar">
      {months.map((m, idx) => (
        <div key={m.period} className="flex flex-col items-center gap-1" title={`${MONTH_ABBR[idx]}: ${MONTH_STATUS_LABEL[m.status] || m.status}`}>
          <div className={`w-4 h-4 rounded-sm ${MONTH_STATUS_STYLE[m.status] || "bg-[#F2EFEA]"}`} />
          <span className="text-[9px] text-[#9C978E] leading-none">{MONTH_ABBR[idx][0]}</span>
        </div>
      ))}
    </div>
  );
}

function RequirementList({ requirements }) {
  if (!requirements || requirements.length === 0) return null;
  return (
    <ul className="space-y-2">
      {requirements.map((r, idx) => (
        <li key={idx} className="flex items-start gap-2 text-sm">
          {r.met ? (
            <CheckCircle size={16} weight="fill" className="text-[#8A9A86] mt-0.5 shrink-0" />
          ) : (
            <WarningCircle size={16} weight="fill" className="text-[#B85C5C] mt-0.5 shrink-0" />
          )}
          <div>
            <p className={r.met ? "text-[#4A5546]" : "text-[#2D2A26]"}>{r.label}</p>
            <p className={`text-xs font-mono mt-0.5 ${r.met ? "text-[#8A9A86]" : "text-[#B85C5C]"}`}>
              Actual: {r.unit === "€" ? formatEUR(r.current) : formatNum(r.current)}
              {!r.met && (
                <>
                  {" · "}
                  Faltan {r.unit === "€" ? formatEUR(r.faltan) : formatNum(r.faltan)}
                </>
              )}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function MonthFailDetail({ months }) {
  const failedMonths = (months || []).filter((m) => m.status === "failed" && m.requirements?.length);
  if (failedMonths.length === 0) return null;
  return (
    <div className="mt-3 space-y-2">
      {failedMonths.map((m) => {
        const idx = parseInt(m.period.slice(5, 7), 10) - 1;
        return (
          <div key={m.period} className="bg-[#FBF4F2] border border-[#B85C5C]/20 rounded-lg p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#B85C5C] mb-2">
              {MONTH_LONG[idx]} {m.period.slice(0, 4)} — no cumplió
            </p>
            <RequirementList requirements={m.requirements} />
          </div>
        );
      })}
    </div>
  );
}

function RiskCard({ item }) {
  return (
    <div
      className="bg-white border border-[#B85C5C]/30 rounded-xl p-4 flex items-start gap-4"
      data-testid={`risk-${item.id_consultor}`}
    >
      <div className="w-1 self-stretch rounded-full bg-[#B85C5C]" />
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-[#2D2A26]">{item.nome}</p>
          <RankBadge rank={item.titulo} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#B85C5C] bg-[#B85C5C]/10 px-1.5 py-0.5 rounded">
            Incumple {item.failed_count}/{item.total_requirements}
          </span>
          {item.fails_this_year != null && (
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                item.fails_this_year >= 3
                  ? "text-white bg-[#B85C5C]"
                  : item.fails_this_year === 2
                  ? "text-[#9C6A2F] bg-[#E3A569]/20"
                  : "text-[#736F68] bg-[#F2EFEA] border border-[#E5E0D8]"
              }`}
              title="Incumplimientos del objetivo mensual de su rango en el año en curso (máx. 3, se reinicia al cambiar de rango)"
            >
              {item.fails_this_year}/3 este año
            </span>
          )}
          {item.coordenador && (
            <span className="text-[10px] text-[#736F68] bg-[#F2EFEA] px-1.5 py-0.5 rounded border border-[#E5E0D8]">
              {item.coordenador}
            </span>
          )}
        </div>
        <StatusCalendar months={item.status_months} />
        <div className="mt-3">
          <RequirementList requirements={item.requirements || item.missing} />
        </div>
        <MonthFailDetail months={item.status_months} />
        {item.lider_subequipa && (
          <p className="mt-3 text-xs text-[#736F68]">Líder directa: {item.lider_subequipa}</p>
        )}
      </div>
    </div>
  );
}

function SponsorCard({ g }) {
  const [open, setOpen] = useState(false);
  const ratio = g.total > 0 ? Math.round((g.activas / g.total) * 100) : 0;
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden" data-testid={`sponsor-${g.sponsor}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-4 text-left flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#2D2A26] truncate">{g.sponsor}</p>
          <div className="mt-1 flex items-center gap-3 text-xs text-[#736F68]">
            <span className="inline-flex items-center gap-1 font-mono">
              <Users size={12} /> {g.total}
            </span>
            <span className="inline-flex items-center gap-1 font-mono text-[#4A5546]">
              <CheckCircle size={12} weight="fill" /> {g.activas} activas
            </span>
            {g.inactivas > 0 && (
              <span className="font-mono text-[#B85C5C]">{g.inactivas} inactivas</span>
            )}
            {g.lideres_junior > 0 && (
              <span className="font-mono">· {g.lideres_junior} Júnior</span>
            )}
            {g.lideres_ejecutivos > 0 && (
              <span className="font-mono">· {g.lideres_ejecutivos} Ejec.</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-mono text-[#2D2A26]">{formatEUR(g.ventas_personales)}</p>
          <p className="text-[10px] text-[#736F68] uppercase tracking-wide">{ratio}% activas</p>
        </div>
        <span className="text-[#736F68]">
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
        </span>
      </button>
      {open && (
        <div className="border-t border-[#F2EFEA] divide-y divide-[#F2EFEA]">
          {g.consultoras
            .sort((a, b) => Number(b.active) - Number(a.active) || b.ventas_pessoais - a.ventas_pessoais)
            .map((c) => (
              <div key={c.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${c.active ? "bg-[#8A9A86]" : "bg-[#D5CFC5]"}`}
                  />
                  <p className="text-[#2D2A26] truncate">{c.nome}</p>
                  {c.titulo !== "Consultor" && <RankBadge rank={c.titulo} />}
                </div>
                <p className="font-mono text-xs text-[#736F68]">{formatEUR(c.ventas_pessoais)}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function CierreMes() {
  const { selected, isGlobal, selectedPais } = useSnapshot();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const req = isGlobal ? fetchGlobalCierreMes(selectedPais) : fetchCierreMes(selected?.id);
    req.then((d) => {
      setData(d);
      setLoading(false);
    });
  };
  useEffect(() => {
    load();
  }, [selected?.id, isGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-6 md:p-10 text-[#736F68]">Cargando previsión...</div>;
  if (data?.empty)
    return (
      <div className="p-6 md:p-10 text-[#736F68]">
        Sube primero un CSV para ver la previsión del cierre de mes.
      </div>
    );

  const k = data.kpis;

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="cierre-mes-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
            Previsión · Periodo {data.snapshot.period}
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
            Cierre de mes
          </h1>
          <p className="mt-1 text-[#736F68] text-sm">
            Quién está cerca de subir, quién corre riesgo y resumen por sponsor — tu guía para cerrar el mes.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-[#E5E0D8] hover:bg-[#F2EFEA] self-start"
          data-testid="refresh-cierre-btn"
        >
          <ArrowsClockwise size={14} />
          Refrescar
        </button>
      </header>

      {/* Summary KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={Confetti}
          label="Listas para subir"
          value={k.ascenso_inminente}
          accent="#8A9A86"
          testid="cierre-kpi-ready"
        />
        <SummaryCard
          icon={Crown}
          label="Cerca de ascender"
          value={k.candidatas_ascenso - k.ascenso_inminente}
          accent="#E3A569"
          testid="cierre-kpi-close"
        />
        <SummaryCard
          icon={WarningCircle}
          label="En riesgo de bajar"
          value={k.en_riesgo}
          accent="#B85C5C"
          testid="cierre-kpi-risk"
        />
        <SummaryCard
          icon={Clock}
          label="Sin actividad"
          value={k.sin_actividad}
          accent="#736F68"
          testid="cierre-kpi-inactive"
        />
      </section>

      <Tabs defaultValue={data.smart_alerts?.length > 0 ? "alertas" : "ascensos"}>
        <TabsList className="bg-[#F2EFEA] flex-wrap h-auto">
          {data.smart_alerts?.length > 0 && (
            <TabsTrigger value="alertas" data-testid="tab-smart-alerts">
              <Sparkle size={14} className="mr-1.5" /> Alertas inteligentes ({data.smart_alerts.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="ascensos" data-testid="tab-ascensos">
            <Crown size={14} className="mr-1.5" /> Ascensos ({data.promoting_close.length + data.promoting_ready.length})
          </TabsTrigger>
          <TabsTrigger value="riesgos" data-testid="tab-riesgos">
            <WarningCircle size={14} className="mr-1.5" /> Riesgos ({data.losing_rank.length})
          </TabsTrigger>
          <TabsTrigger value="sponsors" data-testid="tab-sponsors">
            <Users size={14} className="mr-1.5" /> {isGlobal ? "Por coordinadora" : "Por sponsor"} ({data.sponsor_summary.length})
          </TabsTrigger>
          <TabsTrigger value="actividad" data-testid="tab-actividad">
            <Clock size={14} className="mr-1.5" /> Sin actividad ({data.sin_actividad.length + data.nunca_pedido.length})
          </TabsTrigger>
          {data.growth?.length > 0 && (
            <TabsTrigger value="crecimiento" data-testid="tab-crecimiento">
              <TrendUp size={14} className="mr-1.5" /> Crecimiento ({data.growth.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* SMART ALERTS */}
        <TabsContent value="alertas" className="mt-5 space-y-3">
          {(data.smart_alerts || []).map((a, idx) => (
            <SmartAlert key={idx} alert={a} />
          ))}
          {(!data.smart_alerts || data.smart_alerts.length === 0) && (
            <p className="text-sm text-[#736F68] bg-white border border-[#E5E0D8] rounded-xl p-6">
              Sin alertas inteligentes este mes.
            </p>
          )}
        </TabsContent>

        {/* ASCENSOS */}
        <TabsContent value="ascensos" className="mt-5 space-y-6">
          {data.promoting_ready.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#4A5546] font-display flex items-center gap-2 mb-3">
                <Confetti size={14} weight="fill" /> Ya cumplen — confirmar ascenso ({data.promoting_ready.length})
              </h3>
              <div className="space-y-3">
                {data.promoting_ready.map((it) => (
                  <PromoCard key={it.id_consultor} item={it} ready />
                ))}
              </div>
            </div>
          )}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display mb-3">
              Cerca de subir ({data.promoting_close.length})
            </h3>
            {data.promoting_close.length === 0 ? (
              <p className="text-sm text-[#736F68] bg-white border border-[#E5E0D8] rounded-xl p-6">
                Nadie está suficientemente cerca este mes.
              </p>
            ) : (
              <div className="space-y-3">
                {data.promoting_close.map((it) => (
                  <PromoCard key={it.id_consultor} item={it} />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* RIESGOS */}
        <TabsContent value="riesgos" className="mt-5 space-y-3">
          {data.losing_rank.length === 0 ? (
            <p className="text-sm text-[#736F68] bg-white border border-[#E5E0D8] rounded-xl p-6">
              Sin líderes en riesgo de bajar de estatus este mes 🎉
            </p>
          ) : (
            data.losing_rank.map((it) => <RiskCard key={it.id_consultor} item={it} />)
          )}
        </TabsContent>

        {/* SPONSORS */}
        <TabsContent value="sponsors" className="mt-5 space-y-3">
          <p className="text-xs text-[#736F68]">
            {isGlobal
              ? "Resumen de equipo y actividad por coordinadora. Pulsa para desplegar el detalle."
              : "Cuántas activas tiene cada líder directa. Pulsa para desplegar el detalle del sub-equipo."}
          </p>
          {data.sponsor_summary.map((g) => (
            <SponsorCard key={g.sponsor} g={g} />
          ))}
        </TabsContent>

        {/* SIN ACTIVIDAD */}
        <TabsContent value="actividad" className="mt-5 space-y-6">
          {data.sin_actividad.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display mb-3">
                Pedidos por debajo de 150€ ({data.sin_actividad.length})
              </h3>
              <div className="bg-white border border-[#E5E0D8] rounded-xl divide-y divide-[#F2EFEA]">
                {data.sin_actividad.map((c) => (
                  <div key={c.id} className="p-4 flex items-center justify-between" data-testid={`inactive-${c.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-[#2D2A26]">{c.nome}</p>
                      <p className="text-xs text-[#736F68]">
                        {isGlobal && c.coordenador && <span className="font-medium text-[#C57B67]">{c.coordenador} · </span>}
                        Líder: {c.lider || "—"} · Último pedido: {c.ultimo_pedido?.slice(0, 10) || "—"}
                      </p>
                    </div>
                    <p className="font-mono text-sm text-[#B85C5C]">{formatEUR(c.ventas_pessoais)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.nunca_pedido.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display mb-3">
                Sin ningún pedido ({data.nunca_pedido.length})
              </h3>
              <div className="bg-white border border-[#E5E0D8] rounded-xl divide-y divide-[#F2EFEA]">
                {data.nunca_pedido.map((c) => (
                  <div key={c.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#2D2A26]">{c.nome}</p>
                      <p className="text-xs text-[#736F68]">
                        {isGlobal && c.coordenador && <span className="font-medium text-[#C57B67]">{c.coordenador} · </span>}
                        Líder: {c.lider || "—"}
                      </p>
                    </div>
                    <RankBadge rank={c.titulo} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* CRECIMIENTO */}
        {data.growth?.length > 0 && (
          <TabsContent value="crecimiento" className="mt-5 space-y-3">
            <p className="text-xs text-[#736F68]">
              Mayor crecimiento de ventas personales respecto al mes anterior.
            </p>
            <div className="bg-white border border-[#E5E0D8] rounded-xl divide-y divide-[#F2EFEA]">
              {data.growth.map((g) => (
                <div key={g.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#2D2A26]">{g.nome}</p>
                    <p className="text-xs text-[#736F68] font-mono">
                      {formatEUR(g.anterior)} → {formatEUR(g.actual)}
                    </p>
                  </div>
                  <p className="font-mono text-sm text-[#8A9A86] font-semibold">+{formatEUR(g.delta)}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SmartAlert({ alert }) {
  const palette = {
    alta: { bg: "bg-[#B85C5C]/10", text: "text-[#B85C5C]", border: "border-[#B85C5C]/30" },
    media: { bg: "bg-[#E3A569]/15", text: "text-[#9C6A2F]", border: "border-[#E3A569]/30" },
    baja: { bg: "bg-[#F2EFEA]", text: "text-[#736F68]", border: "border-[#E5E0D8]" },
  };
  const p = palette[alert.severity] || palette.media;
  return (
    <div className={`bg-white border rounded-xl p-4 flex items-start gap-3 ${p.border}`} data-testid="smart-alert-item">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${p.bg} ${p.text}`}>
        <Sparkle size={18} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#2D2A26]">{alert.titulo}</p>
        <p className="text-sm text-[#736F68] mt-0.5">{alert.mensaje}</p>
        <p className="text-xs text-[#C57B67] mt-2 font-medium">→ {alert.accion}</p>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, accent, testid }) {  return (
    <div
      className="bg-white border border-[#E5E0D8] rounded-xl p-4 flex items-center gap-3"
      data-testid={testid}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${accent}1A`, color: accent }}
      >
        <Icon size={20} weight="duotone" />
      </div>
      <div>
        <p className="text-2xl font-semibold font-display text-[#2D2A26] leading-none">{value}</p>
        <p className="text-[10px] uppercase tracking-wider text-[#736F68] font-display font-semibold mt-1">
          {label}
        </p>
      </div>
    </div>
  );
}